import {createSlice} from '@reduxjs/toolkit';
import {USER} from '../../actions/User/actionTypes';

const usersInitialState = {
    user: {
        data: {
            user: [],
            assessors: [],
            assessorSkills: [],
            userAssessor: []
        },
        dashboard: {
            details: {}
        },
        preferences: {
            data: []
        },
        userDetails: [],
        notifyUser: [],
        userExist: [],
        isLoading: false,
        errors: '',
        message: ''
    }
};

export const usersSlice = createSlice({
    name: USER,
    initialState: usersInitialState,
    reducers: {
        /* This action will trigger our saga middleware
           and set the loader to true and reset error message.
        */
        getUserAction: (state) => {
            state.user.isLoading = true;
            state.user.errors = '';
        },
        getUserSuccessAction: (state, {payload}) => {
            state.user.isLoading = false;

            if (payload == null || payload.length === 0) {
                state.user.errors = 'User Id does not exist !';
                state.user.data.user = [];
            } else {
                let alreadyAssignedQids = '';
                payload.forEach((item, index) => {
                    if (item.assessorSamId === null || payload[0].assessorSamId === '') {
                        state.user.data.user = payload;
                    } else {
                        alreadyAssignedQids = alreadyAssignedQids + ',' + item.samId;
                    }
                });
                alreadyAssignedQids = alreadyAssignedQids.slice(1);
                if (alreadyAssignedQids !== '') {
                    state.user.errors = 'Users (' + alreadyAssignedQids + ') already assigned to Assessor !';
                }
            }
        },
        getUserManagementSuccessAction: (state, action) => {

            if (action.payload) {
                state.user.data.assessmentCycleList = action.payload.assessmentCyclList;
                state.user.data.usersList = action.payload.userList;
            }
            state.user.isLoading = false;
        },
        getUserManagementFailureAction: (state, action) => {
            state.user.isLoading = false;
            state.user.errors = action.payload;
        },
        getUserFailureAction: (state, action) => {
            state.user.isLoading = false;
            state.user.errors = action.payload;
        },
        getUserByAssessorAction: (state) => {
            state.user.isLoading = true;
            state.user.errors = '';
        },
        getUserByAssessorSuccessAction: (state, {payload}) => {
            state.user.isLoading = false;
            const raw = payload?.data ?? payload;
            const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
            state.user.data.userAssessor = list;
        },
        getUserByAssessorFailureAction: (state, action) => {
            state.user.isLoading = false;
            state.user.errors = action.payload;
        },
        getAssessorEmployeeMappingAction: (state) => {
            state.user.isLoading = true;
            state.user.errors = '';
        },
        getAssessorEmployeeMappingSuccessAction: (state, {payload}) => {
            state.user.isLoading = false;
            // .NET view model: assessorslist / AssessorSkills; tolerate array-only mis-shapes.
            const p = payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {};
            state.user.data.assessors = p.assessorslist ?? p.Assessorslist ?? [];
            state.user.data.assessorSkills = p.assessorSkills ?? p.AssessorSkills ?? [];
        },
        getAssessorEmployeeMappingFailureAction: (state, action) => {
            state.user.isLoading = false;
            state.user.errors = action.payload;
        },
        resetUsersAction: (state, {payload}) => {
            state.user.isLoading = false;
            state.user.errors = '';
            state.user.message = '';
            if (payload) {
                state.user.data[payload] = [];
            }
        },
        setUserError: (state, {payload}) => {
            state.user.errors = payload;
        },
        updateAssessorAction: (state) => {
            state.user.isLoading = true;
            state.user.errors = '';
        },
        updateAssessorSuccessAction: (state, {payload}) => {
            state.user.isLoading = false;
            if (payload.isUpdate) {
                const list = Array.isArray(payload.data) ? payload.data : [];
                if (list.length > (state.user.data.userAssessor?.length ?? 0)) {
                    state.user.message = 'Successfully added employee!';
                }
                state.user.data.user = [];
                state.user.data.userAssessor = list;
            } else {
                const d = payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data) ? payload.data : {};
                const result = d.result ?? d.Result;
                const unsuccessful = d.UnsucessfullList ?? d.unsucessfullList ?? '';
                const usersList = d.usersList ?? d.UsersList;
                if (result === 'NotSuccessed' || result === 'NotSucceeded') {
                    state.user.errors = `You can not remove this employee (${unsuccessful})!`;
                } else {
                    state.user.message = 'Successfully removed employee!';
                }
                if (Array.isArray(usersList)) {
                    state.user.data.userAssessor = usersList;
                }
                state.user.data.user = Array.isArray(payload.user) ? payload.user : [];
            }
        },
        updateAssessorFailureAction: (state, action) => {
            state.user.isLoading = false;
            state.user.errors = action.payload;
        },

        getUserDetails: (state) => {
            state.user.isLoading = true;
            state.user.errors = '';
        },
        getUserDetailsSuccessAction: (state, {payload}) => {
            state.user.isLoading = false;
            // Python / .NET may return data: null (e.g. 302 redirect payload); null breaks consumers that read details.skills.
            state.user.dashboard.details =
                payload != null && typeof payload === 'object' && !Array.isArray(payload) ? payload : {};
        },
        getUserDetailsFailureAction: (state, action) => {
            state.user.isLoading = false;
            state.user.errors = action.payload;
        },

        getUserProfile: (state) => {
            state.user.isLoading = true;
            state.user.errors = '';
        },
        getUserProfileSuccessAction: (state, {payload}) => {
            state.user.isLoading = false;
            state.user.dashboard.userProfile = payload;
        },
        getUserProfileFailureAction: (state, action) => {
            state.user.isLoading = false;
            state.user.errors = action.payload;
        },

        resetUpdateUserPreferenceAction: (state) => {
            state.user.message = '';
            state.user.errors = '';
        },

        updateUserPreference: (state) => {
            state.user.isLoading = true;
            state.user.errors = '';
        },
        updateUserPreferenceSuccessAction: (state, {payload}) => {
            state.user.isLoading = false;
            state.user.preferences.data = payload;
            if (payload) {
                state.user.message = 'Saved Successfully';
            }
        },
        updateUserPreferenceFailureAction: (state, action) => {
            state.user.isLoading = false;
            state.user.errors = action.payload;
        },
        submitSkillChangeRequest: (state) => {
            state.user.isLoading = true;
            state.user.errors = '';
        },
        submitSkillChangeRequestSuccessAction: (state, { payload }) => {
            state.user.isLoading = false;
            if (payload) {
                state.user.message = payload;
            }
        },
        submitSkillChangeRequestFailureAction: (state, action) => {
            state.user.isLoading = false;
            state.user.errors = action.payload;
        },
        saveUser: (state) => {
            state.user.isLoading = true;
            state.user.errors = '';
        },
        saveUserSuccessAction: (state, {payload}) => {
            state.user.isLoading = false;
            state.user.userDetails = payload;
            if (payload) {
                state.user.message = 'Saved Successfully';
            }
        },
        saveUserFailureAction: (state, action) => {
            state.user.isLoading = false;
            state.user.errors = action.payload;
        },
        notifyUser: (state) => {
            state.user.isLoading = true;
            state.user.errors = '';
        },
        notifyUserSuccessAction: (state, {payload}) => {
            state.user.isLoading = false;
            state.user.notifyUser = payload;
            if (payload) {
                state.user.message = 'Saved Successfully';
            }
        },
        notifyUserFailureAction: (state, action) => {
            state.user.isLoading = false;
            state.user.errors = action.payload;
        },
        checkUserExist: (state) => {
            state.user.isLoading = true;
            state.user.errors = '';
        },
        checkUserExistSuccessAction: (state, {payload}) => {
            state.user.isLoading = false;
            state.user.userExist = payload;
        },
        checkUserExistFailureAction: (state, action) => {
            state.user.isLoading = false;
            state.user.errors = action.payload;
        },
        resetCheckUserExistAction: (state) => {
            state.user.userExist = [];
            state.user.isLoading = false;
        },
    }
});

/* getUserSuccessAction and getUserErrorAction will be used inside the saga
  middleware. Only getUserAction will be used in a React component.
*/

export const {
    getUserAction,
    getUserSuccessAction,
    getUserManagementSuccessAction,
    getUserManagementFailureAction,
    getUserFailureAction,
    resetUsersAction,
    getAssessorEmployeeMappingAction,
    getAssessorEmployeeMappingFailureAction,
    getAssessorEmployeeMappingSuccessAction,
    getUserByAssessorAction,
    getUserByAssessorFailureAction,
    getUserByAssessorSuccessAction,
    setUserError,
    updateAssessorAction,
    updateAssessorFailureAction,
    updateAssessorSuccessAction,
    getUserDetails,
    getUserDetailsSuccessAction,
    getUserDetailsFailureAction,
    getUserProfile,
    getUserProfileSuccessAction,
    getUserProfileFailureAction,
    updateUserPreference,
    updateUserPreferenceSuccessAction,
    updateUserPreferenceFailureAction,
    submitSkillChangeRequest,
    submitSkillChangeRequestSuccessAction,
    submitSkillChangeRequestFailureAction,
    resetUpdateUserPreferenceAction,
    saveUser,
    saveUserSuccessAction,
    saveUserFailureAction,
    notifyUser,
    notifyUserSuccessAction,
    notifyUserFailureAction,
    checkUserExist,
    checkUserExistSuccessAction,
    checkUserExistFailureAction,
    resetCheckUserExistAction,
} = usersSlice.actions;
export default usersSlice.reducer;
